import {isUndefined} from 'lodash';
import {connect} from 'react-redux';

import {isDefined} from '../../../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {
  BulkActionsTrackingTypesEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../../../../../libs/shared/core/src/services/tracking/tracking';
import {ResolveStateProps} from '../../../../../../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {ResolveDispatchProps} from '../../../../../../../libs/shared/core/src/store/helpers/reduxHelpers';
import {CellsContext} from '../../../../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
} from '../../../../../../../srcCommon/helpers/reselectHelpers';
import {ViewerDropdownInterfaceEventSourcesEnum} from '../../../../../../helpers/models/tagHelpers';
import {ConversationBaseModel} from '../../../../../../models/api/conversationModel';
import {AppState} from '../../../../../../store/appState';
import {getCompanyUrl} from '../../../../../../store/data/company/companySelectors';
import {
  assignToTeammateAction,
  assignToTeammateBulkAction,
} from '../../../../../../store/data/conversations/assigneeConversationsActions';
import {makeGetAssigneeForAll} from '../../../../../../store/data/conversations/conversationsMultiSelectors';
import {makeGetConversationInboxIdsForTeammates} from '../../../../../../store/data/inboxes/inboxesSelectors';
import {makeGetIsTeamMemberForConversations} from '../../../../../../store/data/teammates/mentionTeammatesSelectors';
import {getFindOnlineStatus} from '../../../../../../store/data/teammates/onlineTeammatesSelectors';
import {getUser} from '../../../../../../store/data/teammates/teammatesSelectors';
import {InboxesParameters} from '../../../../../../store/panels/inboxes/parameters/inboxesParameters';
import {AssignOptions, LazyAssignDropdown} from './lazyAssignDropdown';

/*
 * Props.
 */

interface AssignDropdownContainerProps {
  cellsContext: CellsContext;
  conversations: ReadonlyArray<ConversationBaseModel>;
  isBulkActionRequired: boolean;
  inboxesParameters: InboxesParameters;
  onInterfaceEvent?: (action: BulkActionsTrackingTypesEnum, isBulk: boolean) => void;
  interfaceEventSource: ViewerDropdownInterfaceEventSourcesEnum;
}

/*
 * Selectors.
 */

function getConversations(_state: AppState, props: AssignDropdownContainerProps) {
  return props.conversations;
}

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<AssignDropdownContainerProps>()(() => {
  const getAssigneeAll = makeGetAssigneeForAll(getConversations);

  return {
    user: getUser,
    isTeamMember: makeGetIsTeamMemberForConversations(getConversations),
    findOnlineStatus: getFindOnlineStatus,
    assigneeAll: getAssigneeAll,
    conversationInboxIds: makeGetConversationInboxIdsForTeammates(getConversations),
    companyUrl: getCompanyUrl,
  };
});

const mapDispatchToProps = makeMapDispatchToProps<AssignDropdownContainerProps>()(() => ({
  assignToTeammate: assignToTeammateAction,
  assignToTeammateBulk: assignToTeammateBulkAction,
}));

function mergeProps(
  stateProps: ResolveStateProps<typeof mapStateToProps>,
  dispatchProps: ResolveDispatchProps<typeof mapDispatchToProps>,
  ownProps: AssignDropdownContainerProps,
) {
  const {
    cellsContext,
    conversations,
    isBulkActionRequired,
    inboxesParameters,
    onInterfaceEvent,
    interfaceEventSource,
  } = ownProps;
  const {assigneeAll} = stateProps;

  const onAssign: (options: AssignOptions) => void = (params: AssignOptions) => {
    const {teammate, openedAt, interfaceEventSection} = params;
    if (onInterfaceEvent) {
      onInterfaceEvent(
        isUndefined(teammate) ? BulkActionsTrackingTypesEnum.UNASSIGN : BulkActionsTrackingTypesEnum.ASSIGN,
        false,
      );
    }

    // If the new assignee is the same, nothing to do.
    if (isDefined(assigneeAll) && teammate === assigneeAll) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_ASSIGN_USING_DROPDOWN, {
      num_conversations: conversations.length,
      teammate_id: teammate ? teammate.id : null,
      time_til_assign: performance.now() - (openedAt ?? 0),
      section: interfaceEventSection,
      is_bulk: isBulkActionRequired,
      location: interfaceEventSource,
    });

    // If the specified teammate is undefined, unassign.
    const conversationIds = conversations.map((c) => c.id);
    if (isUndefined(teammate)) {
      dispatchProps.assignToTeammate(conversationIds, undefined, {cellsContext});
      return;
    }

    // Otherwise, assign.
    dispatchProps.assignToTeammate(conversationIds, teammate.id, {cellsContext});
  };

  const onBulkAssign: (options: AssignOptions) => void = (params: AssignOptions) => {
    const {teammate, openedAt, interfaceEventSection} = params;
    if (onInterfaceEvent) {
      onInterfaceEvent(
        isUndefined(teammate) ? BulkActionsTrackingTypesEnum.UNASSIGN : BulkActionsTrackingTypesEnum.ASSIGN,
        true,
      );
    }

    trackInterfaceEvent(InterfaceEventsEnum.P3_ASSIGN_USING_DROPDOWN, {
      num_conversations: conversations.length,
      teammate_id: teammate ? teammate.id : null,
      time_til_assign: performance.now() - (openedAt ?? 0),
      section: interfaceEventSection,
      is_bulk: isBulkActionRequired,
      location: interfaceEventSource,
    });

    // If the specified teammate is undefined, unassign.
    if (isUndefined(teammate)) {
      dispatchProps.assignToTeammateBulk(inboxesParameters, cellsContext, undefined);
      return;
    }

    // Otherwise, assign.
    dispatchProps.assignToTeammateBulk(inboxesParameters, cellsContext, teammate.id);
  };

  return {
    ...stateProps,
    ...ownProps,
    onAssign,
    onBulkAssign,
    assigneeAll,
  };
}

export const LazyAssignDropdownContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(LazyAssignDropdown);
